/* eslint no-param-reassign: 0 */
import {
  flatten,
  get,
  pick,
  some,
  unionBy,
} from 'lodash';
import SubscriptionApi from 'client/api/v2/SubscriptionApi';
import SubscriptionConstants from 'client/Payments/pricingMatrix/SubscriptionConstants';
import createModel from 'client/store/modules/models/createModel';

const {
  getProductCodeByType,
  AttributeDetails,
  AddOnStates,
  SubscriptionFields,
  SubscriptionTypes,
} = SubscriptionConstants;

export const getDefaultItem = () => ({
  addOns: [],
  displayName: '',
  listPrice: 0,
  productCode: '',
});

const model = createModel(getDefaultItem, 'productCode', SubscriptionApi);

const isDefaultAddOn = (state, productCode) => some(state.item.components, ['productCode', productCode]);

// Check to make sure "requiresAtLeastOneAddOn" addons (ex Premium Support) have valid prereqs
function handleRequiresAtLeastOneAddOn(context) {
  const { state } = context;

  const areAnyNonRequriresAtLeastOneAddOnsSelected = state.item.addOns
    .filter(addOn => !isDefaultAddOn(state, addOn.productCode) && !addOn.requiresAtLeastOneAddOn)
    .length > 0;
  const selectedRequiresAtLeastOneAddOns = state.item.addOns
    .filter(addOn => addOn.requiresAtLeastOneAddOn);
  const areAnyRequiresAtLeastOneAddOnsSelected = selectedRequiresAtLeastOneAddOns.length > 0;

  // If the only thing selected are addons that requires anything then
  // these addons no longer have their prereqs and should be removed
  if (areAnyRequiresAtLeastOneAddOnsSelected && !areAnyNonRequriresAtLeastOneAddOnsSelected) {
    selectedRequiresAtLeastOneAddOns.forEach(addOn => {
      context.commit('REMOVE_ADD_ON', addOn);
    });
  }
}

function handleAddonsWithPrereqs(context) {
  const { addOns } = context.state.item;
  addOns
    .filter(addOn => addOn.requires)
    .forEach(addOnWithRequirements => {
      // This will only remove the addon if none of the required addons are selected (OR)
      const productCodeIsRequired = (productCode) => addOnWithRequirements.requires
        .includes(productCode);
      if (!addOns.some(selectedAddOn => productCodeIsRequired(selectedAddOn.productCode))) {
        context.commit('REMOVE_ADD_ON', addOnWithRequirements);
      }
    });
}

async function ensureAllRequirementsFulfilled(context) {
  const { state } = context;

  if (state.item.addOns?.length > 0) {
    if (state.item.productCode === getProductCodeByType(SubscriptionTypes.alaCarte)
      || state.item.productCode === getProductCodeByType(SubscriptionTypes.sso)
      || state.item.productCode === getProductCodeByType(SubscriptionTypes.coreDirectory)) {
      handleAddonsWithPrereqs(context);
    }

    if (state.item.productCode === getProductCodeByType(SubscriptionTypes.alaCarte)) {
      handleRequiresAtLeastOneAddOn(context);
    }
  }
}

export default {
  namespaced: true,

  state: {
    ...model.state,
    latestAddOnChange: null,
  },

  actions: {
    ...model.actions,

    async toggleAddOn(context, addOn) {
      const addOns = get(context.state, 'item.addOns', []);

      if (addOns.find(existingAddOn => existingAddOn.productCode === addOn.productCode)) {
        context.commit('REMOVE_ADD_ON', addOn);
        await ensureAllRequirementsFulfilled(context);
      } else {
        context.commit('ADD_ADD_ON', addOn);
      }
    },

    updateItem(context, sub) {
      const subscription = pick(sub, Object.values(SubscriptionFields));

      if (subscription.components?.length) {
        subscription.addOns = unionBy(
          subscription.addOns,
          'productCode',
        );
      }
      model.actions.updateItem(context, subscription);
    },
  },

  getters: {
    ...model.getters,

    getAddOnProductCodes(state) {
      if (!state.item.addOns) return [];
      return state.item.addOns.map(addOn => addOn.productCode);
    },

    getAllFeatures(state) {
      const basePlanFeatures = state.item.features || [];
      const addOnFeatures = state.item.addOns.map((addOn) => addOn.features);
      return flatten(basePlanFeatures.concat(addOnFeatures));
    },

    getLatestAddOnChange(state) {
      return state.latestAddOnChange;
    },

    getSelectedAddOns(state) {
      return state.item.addOns;
    },

    getAllAddOns(state) {
      return state.item.addOns;
    },

    cloudDirectoryAndSupport(state) {
      const components = state.item.components.map((component) => {
        const [feature] = component.features;
        const attribute = AttributeDetails[feature.name];
        return {
          ...component,
          displayName: attribute.name,
          icon: attribute.icon,
        };
      });
      return components[0];
    },

    getAddOnsSubTotal(state, getters, rootState) {
      return rootState.PricingModule.item.isAnnual
        ? getters.getAllAddOns.reduce((acc, addOn) => acc + addOn.annualPrice, 0)
        : getters.getAllAddOns.reduce((acc, addOn) => acc + addOn.listPrice, 0);
    },

    hasSelectedAddons(state, getters) {
      return getters.getSelectedAddOns.length > 0;
    },
  },

  mutations: {
    ...model.mutations,

    ADD_ADD_ON(state = { item: { addOns: [] } }, addOn) {
      state.item.addOns.push(addOn);
      state.latestAddOnChange = {
        changeType: AddOnStates.add,
        ...addOn,
      };
    },

    REMOVE_ADD_ON(state = { item: { addOns: [] } }, addOn) {
      state.item.addOns = state.item.addOns
        .filter(existingAddOn => existingAddOn.productCode !== addOn.productCode);
      state.latestAddOnChange = {
        changeType: AddOnStates.remove,
        ...addOn,
      };
    },

    RESET_ITEM(state) {
      state.latestAddOnChange = null;
      model.mutations.RESET_ITEM(state);
    },
  },
};
